import { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { apiClient } from '@/lib/api-client';
import { Card } from '@/components/ui/Card';
import { useAuthContext } from '@/hooks/useAuthContext';

const CHARTING_ROLES = ['charting_member', 'charting_manager'];

interface NotificationPreference {
  notificationType: string;
  emailEnabled: boolean;
  inAppEnabled: boolean;
  teamsEnabled: boolean;
}

const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  hold_request_created: 'Hold Request Created',
  hold_request_completed: 'Hold Request Completed',
  hold_expired: 'Hold Expired',
  hold_request_comment_added: 'Hold Request Comment Added',
  hold_request_overdue: 'Hold Request Overdue',
  hold_request_revision_required: 'Revision Required',
  template_upload_completed: 'Template Upload Completed',
  line_item_assigned: 'Line Item Assigned to Me',
  line_item_status_changed: 'Line Item Status Changed',
  exception_approval_required: 'Exception Approval Required',
  exception_approved: 'Exception Approved',
  exception_rejected: 'Exception Rejected',
};

export function NotificationPreferences() {
  const { user } = useAuthContext();
  const isChartingUser = user ? CHARTING_ROLES.includes(user.role) : false;

  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get('/notifications/preferences');
      // Sort preferences to match the display order defined in NOTIFICATION_TYPE_LABELS
      const labelOrder = Object.keys(NOTIFICATION_TYPE_LABELS);
      const sorted = [...response.data.preferences].sort((a: NotificationPreference, b: NotificationPreference) => {
        const aIdx = labelOrder.indexOf(a.notificationType);
        const bIdx = labelOrder.indexOf(b.notificationType);
        return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
      });
      setPreferences(sorted);
    } catch (err) {
      console.error('Error loading preferences:', err);
      setError('Failed to load notification preferences');
    } finally {
      setLoading(false);
    }
  };

  const updatePreference = async (
    notificationType: string,
    channel: 'inAppEnabled' | 'emailEnabled',
    value: boolean
  ) => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Optimistically update UI
      setPreferences((prev) =>
        prev.map((pref) =>
          pref.notificationType === notificationType
            ? { ...pref, [channel]: value }
            : pref
        )
      );

      // Get current preference
      const currentPref = preferences.find(
        (p) => p.notificationType === notificationType
      );

      if (!currentPref) return;

      // Send update to server — preserve teamsEnabled unchanged
      await apiClient.put(`/notifications/preferences/${notificationType}`, {
        emailEnabled: channel === 'emailEnabled' ? value : currentPref.emailEnabled,
        inAppEnabled: channel === 'inAppEnabled' ? value : currentPref.inAppEnabled,
        teamsEnabled: currentPref.teamsEnabled,
      });

      setSuccessMessage('Preferences updated successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      console.error('Error updating preference:', err);
      setError('Failed to update preference');
      // Revert optimistic update
      loadPreferences();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-outfront-purple"></div>
            <span className="ml-3 text-neutral-600">Loading preferences...</span>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-neutral-900">
            Notification Preferences
          </h2>
          <p className="mt-2 text-sm text-neutral-600">
            Choose how you want to receive notifications for different events.
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">{successMessage}</p>
          </div>
        )}

        <div className="space-y-6">
          {/* Table header — 2 channel columns: Email and In-App */}
          <div className="grid grid-cols-3 gap-4 pb-3 border-b border-neutral-200">
            <div className="col-span-1">
              <h3 className="text-sm font-semibold text-neutral-600">
                Notification Type
              </h3>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center gap-1">
                <h3 className="text-sm font-semibold text-neutral-600">Email</h3>
                {isChartingUser && (
                  <div
                    className="relative inline-block"
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    onFocus={() => setShowTooltip(true)}
                    onBlur={() => setShowTooltip(false)}
                  >
                    <Info
                      className="h-4 w-4 text-neutral-400 cursor-help"
                      tabIndex={0}
                      aria-describedby="email-routing-tooltip"
                      aria-label="Email routing information"
                    />
                    {showTooltip && (
                      <div
                        id="email-routing-tooltip"
                        role="tooltip"
                        className="absolute z-10 bottom-full left-1/2 -translate-x-1/2 mb-2 w-72 px-3 py-2 text-xs text-white bg-neutral-900 rounded-md shadow-lg"
                      >
                        Email notifications are sent to market distribution lists.
                        <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-neutral-900"></div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="text-center">
              <h3 className="text-sm font-semibold text-neutral-600">In-App</h3>
            </div>
          </div>

          {/* Preference rows */}
          {preferences.map((pref) => (
            <div
              key={pref.notificationType}
              className="grid grid-cols-3 gap-4 items-center py-3 border-b border-neutral-100 hover:bg-neutral-50 transition-colors"
            >
              <div className="col-span-1">
                <label className="text-sm text-neutral-900">
                  {NOTIFICATION_TYPE_LABELS[pref.notificationType] ||
                    pref.notificationType}
                </label>
              </div>
              <div className="text-center">
                <input
                  type="checkbox"
                  checked={pref.emailEnabled}
                  disabled={isChartingUser}
                  onChange={isChartingUser ? undefined : (e) =>
                    updatePreference(pref.notificationType, 'emailEnabled', e.target.checked)
                  }
                  className={`h-4 w-4 text-outfront-purple focus:ring-outfront-purple border-neutral-200 rounded ${isChartingUser ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  aria-label={isChartingUser
                    ? `Email for ${NOTIFICATION_TYPE_LABELS[pref.notificationType] || pref.notificationType} — managed by market distribution lists`
                    : `Email for ${NOTIFICATION_TYPE_LABELS[pref.notificationType] || pref.notificationType}`
                  }
                />
              </div>
              <div className="text-center">
                <input
                  type="checkbox"
                  checked={pref.inAppEnabled}
                  onChange={(e) =>
                    updatePreference(
                      pref.notificationType,
                      'inAppEnabled',
                      e.target.checked
                    )
                  }
                  disabled={saving}
                  className="h-4 w-4 text-outfront-purple focus:ring-outfront-purple border-neutral-200 rounded cursor-pointer disabled:opacity-50"
                  aria-label={`In-app notifications for ${NOTIFICATION_TYPE_LABELS[pref.notificationType] || pref.notificationType}`}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 pt-6 border-t border-neutral-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-outfront-purple"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-neutral-600">
                {isChartingUser ? (
                  <>
                    <strong>Email:</strong> Notifications are sent to market
                    distribution lists based on line item market. Email routing is not configurable here.
                  </>
                ) : (
                  <>
                    <strong>Email:</strong> Toggle to control which notification
                    types are sent to your individual email.
                  </>
                )}
                <br />
                <strong>In-App:</strong> Configurable per user. See notifications
                in the application in real-time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
